import React from "react";
import { motion } from "framer-motion";
import { useDashTheme } from "../hooks/useDashTheme";

// Premium app-shell frame for all DashboardV2-family screens.
// Layout:
//   ┌─ Header band (eyebrow + icon + title + subtitle + actions) ─┐
//   ├─ Tab row: [Tab] [Tab] [Tab]              [tools / topRight] ┤
//   └─ Page body (grows; the dashboard main pane scrolls) ─────────┘
// Header + tabs stay with the page. Pass `tabs`
// (+ activeTab/onTabChange) to get the tab row; `topRight` renders opposite
// the tabs, which is where page tools (filters, view switches) belong.
export function PageShell({
  title, subtitle, eyebrow, icon, actions,
  tabs, activeTab, onTabChange, topRight, tabLayoutId = "shellTab",
  children,
}) {
  const t = useDashTheme();

  return (
    <div
      className="dashv2-root"
      style={{ position: "relative", display: "flex", flexDirection: "column", minHeight: "100%", background: t.c.bg, fontFamily: t.font.family, color: t.c.text }}
    >
      {/* ── Pinned page header ── */}
      <div style={{ flexShrink: 0, background: t.c.bg, padding: `${t.s[4]}px ${t.s[6]}px 0` }}>
        <motion.div
          initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: t.motion.ease.out }}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: t.s[4], flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: t.s[3], minWidth: 0 }}>
            {icon && (
              <span style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28,
                            borderRadius: t.r.md, background: t.c.surfaceMuted, color: t.c.textSecondary, flexShrink: 0,
              }}>{icon}</span>
            )}
            <div style={{ minWidth: 0 }}>
              {eyebrow && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 5, marginBottom: 2 }}>
                  <span style={{ width: 5, height: 5, borderRadius: 99, background: t.status.positive }} />
                  <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, fontWeight: 400 }}>{eyebrow}</span>
                </div>
              )}
              <h1 style={{ fontSize: t.font.size["2xl"], fontWeight: t.font.weight.medium, lineHeight: "26px", margin: 0, letterSpacing: "-0.01em" }}>{title}</h1>
              {subtitle && <p style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, margin: "2px 0 0", lineHeight: "16px" }}>{subtitle}</p>}
            </div>
          </div>
          {actions && <div style={{ display: "flex", alignItems: "center", gap: t.s[2], flexWrap: "wrap" }}>{actions}</div>}
        </motion.div>

        {/* ── Tab row + page tools, directly under the title ── */}
        {(tabs || topRight) && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
                        gap: t.s[4], marginTop: t.s[3], height: 34,
                        // The rule belongs to the tabs. Without them it reads as a stray line.
                        borderBottom: tabs ? `1px solid ${t.c.border}` : "none",
                      }}>
            <nav
              className="dashv2-tabbar"
              role="tablist"
              // overflowY hidden: overflowX:auto alone makes the box scroll vertically too,
              // and the 2px active underline extended 1px past it → a 1px vertical scroll.
              style={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0, overflowX: "auto", overflowY: "hidden", height: "100%" }}
            >
              {(tabs || []).map((tab) => {
                const active = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    role="tab"
                    aria-selected={active}
                    onClick={() => onTabChange(tab.key)}
                    style={{
                      position: "relative", display: "inline-flex", alignItems: "center", gap: 6,
                      height: "100%", padding: "0 9px", border: "none", background: "transparent",
                      color: active ? t.c.text : t.c.textSecondary, fontSize: t.font.size.md,
                      fontWeight: active ? 500 : 400, cursor: "pointer",
                      fontFamily: t.font.family, whiteSpace: "nowrap", transition: "color 140ms",
                    }}
                    onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = t.c.text; }}
                    onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = t.c.textSecondary; }}
                  >
                    {tab.icon && <span style={{ display: "inline-flex", color: active ? t.c.iconHover : t.c.icon }}>{tab.icon}</span>}
                    {tab.label}
                    {tab.count != null && (
                      <span style={{
                        fontSize: t.font.size.xs, fontWeight: 500, padding: "1px 5px", borderRadius: t.r.xs,
                        background: t.c.surfaceHover, color: t.c.textSecondary,
                        fontVariantNumeric: "tabular-nums", lineHeight: 1.4,
                      }}>{tab.count}</span>
                    )}
                    {active && (
                      <motion.span layoutId={tabLayoutId} transition={t.motion.spring.snappy}
                        style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 2, background: t.c.icon }} />
                    )}
                  </button>
                );
              })}
            </nav>
            {topRight && <div style={{ display: "flex", alignItems: "center", gap: t.s[2], flexShrink: 0 }}>{topRight}</div>}
          </div>
        )}
      </div>

      {/* ── Scrollable content ── */}
      <div
        className="dashv2-page-scroll"
        style={{ position: "relative", zIndex: 1, flex: "0 0 auto" }}
      >
        <div style={{ width: "100%", padding: `${t.s[5]}px ${t.s[6]}px ${t.s[8]}px` }}>
          {children}
        </div>
      </div>
    </div>
  );
}
